import {ReactNode} from "react";

import ViewKanbanOutlined from "@mui/icons-material/ViewKanbanOutlined";
import ViewKanban from "@mui/icons-material/ViewKanban";
import QueryStatsOutlined from "@mui/icons-material/QueryStatsOutlined";
import QueryStats from "@mui/icons-material/QueryStats";

import GroupOutlined from "@mui/icons-material/GroupOutlined";
import Group from "@mui/icons-material/Group";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import FactCheck from "@mui/icons-material/FactCheck";
import BackupOutlined from "@mui/icons-material/BackupOutlined";
import Backup from "@mui/icons-material/Backup";

import {routes} from "../utils/routes";

/**
 * ============================================================================
 * menuConfig — структура бокового меню.
 * ============================================================================
 *
 * НЕ ПУТАТЬ с routesConfig:
 *   - routesConfig (config/routes.tsx) — карта URL → компонент + защита роута.
 *     Источник истины для React Router. Не группирует.
 *   - menuConfig (этот файл) — порядок и группировка ВИДИМОГО меню.
 *     Источник истины для сайдбара. Не знает про lazy/Suspense.
 *
 * Разделение даёт:
 *   - Возможность переставлять пункты меню без правки роутера.
 *   - Возможность вообще не показывать роут в меню (просто не добавлять сюда).
 *   - Чистый routesConfig без UI-полей (icon/iconActive/group).
 *
 * Иконки:
 *   icon       — outlined, для неактивного состояния.
 *   iconActive — filled, для активного состояния.
 *                Если iconActive не задан, используется icon (без переключения).
 *
 * Видимость:
 *   requiresAdmin   — пункт виден только админам.
 *
 *   Эти же поля могут быть у ГРУППЫ — тогда вся группа скрывается.
 *   Если у группы есть условие И у пунктов внутри — оба должны выполниться.
 * ============================================================================
 */

export interface MenuItemConfig {
    path: string;
    label: string;
    icon: ReactNode;
    iconActive?: ReactNode;
    requiresAdmin?: boolean;
    /** Пункт виден только ролям из списка (role_id, см. models/IUser). */
    requiresRoles?: number[];
}

export interface MenuGroupConfig {
    /** Стабильный id группы — используется как ключ в localStorage для collapsible. */
    id: string;
    /** Заголовок группы (caption uppercase). Если undefined — группа без заголовка. */
    title?: string;
    items: MenuItemConfig[];
    /** Если true — группа сворачиваемая (с ExpandMore триггером). */
    collapsible?: boolean;
    /** Начальное состояние раскрытия (если нет сохранённого в localStorage). */
    defaultOpen?: boolean;
    /** Скрыть всю группу для не-админов. */
    requiresAdmin?: boolean;
    /** Скрыть всю группу для ролей вне списка. */
    requiresRoles?: number[];
    /**
     * Прижать группу к низу sidebar'a. Используется для редко-используемых
     * утилитарных разделов (справочники, документация).
     * Если true — группа рендерится отдельно, в нижней зоне drawer'а.
     */
    pinToBottom?: boolean;
    /**
     * Иконка для триггера сворачиваемой группы (collapsible=true).
     * Для несворачиваемых групп игнорируется.
     */
    icon?: ReactNode;
    iconActive?: ReactNode;
}

export const menuConfig: MenuGroupConfig[] = [
    // ─── 1. Оперативная работа (без заголовка) ──────────────────────────────
    {
        id: "operations",
        items: [
            {
                path: routes.machines,
                label: "Доска",
                icon: <ViewKanbanOutlined/>,
                iconActive: <ViewKanban/>,
            },
            {
                path: routes.stats,
                label: "Стата",
                icon: <QueryStatsOutlined/>,
                iconActive: <QueryStats/>,
            },
        ],
    },

    // ─── 2. Организация (без заголовка) ─────────────────────────────────────
    {
        id: "organization",
        items: [
            {
                path: routes.users,
                label: "Сотрудники",
                icon: <GroupOutlined/>,
                iconActive: <Group/>,
            },
            {
                path: routes.references,
                label: "Эталоны",
                icon: <FactCheckOutlined/>,
                iconActive: <FactCheck/>,
            },
            {
                path: routes.backups,
                label: "Бэкапы",
                icon: <BackupOutlined/>,
                iconActive: <Backup/>,
            },
        ],
    },
];
